import { AxiosRequestConfig } from 'axios';

import { RestApi } from '../../common/services';
import { store } from '../../common/store/store';
import { Issue, IssueCategory, IssueCategoryFormValues, IssueFormValues, IssuePhoto, IssueStatus } from '../types';

const buildingId = () => store.getState().security.ui.buildingId;

export const getIssues = async (): Promise<Issue[]> =>
  RestApi.get<never, Issue[]>(`/building/${buildingId()}/issue`);

export const getIssue = async (id: string): Promise<Issue> =>
  RestApi.get<never, Issue>(`/building/${buildingId()}/issue/${id}`);

/** Vytvoření hlášení – multipart kvůli fotkám (photos[i]). */
export const createIssue = async (values: IssueFormValues): Promise<Issue> => {
  const formData = new FormData();
  formData.append('title', values.title);
  formData.append('description', values.description);
  formData.append('categoryId', values.categoryId);
  if (values.location) formData.append('location', values.location);

  if (values.photos) {
    for (let i = 0; i < values.photos.length; i++) {
      formData.append(`photos[${i}]`, values.photos[i]);
    }
  }

  return RestApi.post<FormData, Issue>(`/building/${buildingId()}/issue`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const updateIssue = async (id: string, values: IssueFormValues): Promise<Issue> =>
  RestApi.patch<Record<string, string | null>, Issue>(`/building/${buildingId()}/issue/${id}`, {
    title: values.title,
    description: values.description,
    categoryId: values.categoryId,
    location: values.location ? values.location : null,
  });

export const deleteIssue = async (id: string): Promise<void> =>
  RestApi.delete<void>(`/building/${buildingId()}/issue/${id}`);

export const changeIssueStatus = async (id: string, status: IssueStatus): Promise<Issue> =>
  RestApi.post<{ status: IssueStatus }, Issue>(`/building/${buildingId()}/issue/${id}/status`, { status });

export const addIssueComment = async (id: string, content: string): Promise<Issue> =>
  RestApi.post<{ content: string }, Issue>(`/building/${buildingId()}/issue/${id}/comment`, { content });

/** Kategorie budovy – member dostane jen aktivní, admin vše včetně kontaktů. */
export const getIssueCategories = async (): Promise<IssueCategory[]> =>
  RestApi.get<never, IssueCategory[]>(`/building/${buildingId()}/issue-category`);

const toCategoryPayload = (values: IssueCategoryFormValues) => ({
  name: values.name,
  icon: values.icon,
  contactEmails: values.contactEmails.map((email) => email.trim()).filter(Boolean),
  active: values.active,
});

export const createIssueCategory = async (values: IssueCategoryFormValues): Promise<IssueCategory> =>
  RestApi.post<ReturnType<typeof toCategoryPayload>, IssueCategory>(
    `/building/${buildingId()}/issue-category`,
    toCategoryPayload(values)
  );

export const updateIssueCategory = async (id: string, values: IssueCategoryFormValues): Promise<IssueCategory> =>
  RestApi.patch<ReturnType<typeof toCategoryPayload>, IssueCategory>(
    `/building/${buildingId()}/issue-category/${id}`,
    toCategoryPayload(values)
  );

export const deleteIssueCategory = async (id: string): Promise<void> =>
  RestApi.delete<void>(`/building/${buildingId()}/issue-category/${id}`);

/** Nové pořadí kategorií (drag & drop) – vrací kompletní seznam v novém pořadí. */
export const reorderIssueCategories = async (categoryIds: string[]): Promise<IssueCategory[]> =>
  RestApi.post<{ categoryIds: string[] }, IssueCategory[]>(`/building/${buildingId()}/issue-category/reorder`, {
    categoryIds,
  });

/**
 * Stáhne fotku (autorizovaně) a vrátí object URL pro <img>. Volající musí URL
 * revokovat. `width` vyžádá kešovanou zmenšeninu (grid karet).
 */
export const fetchIssuePhotoUrl = async (issueId: string, photo: IssuePhoto, width?: number): Promise<string> => {
  const url = `/building/${buildingId()}/issue/${issueId}/photo/${photo.id}/download${width ? `?width=${width}` : ''}`;
  const config: AxiosRequestConfig = { method: 'GET', responseType: 'blob', url };

  const res = await RestApi.axios(config);
  return URL.createObjectURL(res.data);
};
